<template>
  <v-container fluid grid-list-md>
    <v-row justify="center">
      <v-col cols="10">
        <v-card>
          <v-card-title>
            Import Assets
          </v-card-title>
          <v-card-subtitle>
            Select an Excel file to import assets
          </v-card-subtitle>
          <v-card-text>
            <v-col cols="12">
              <v-form
                ref="form"
              >
                <v-row class="my-2 mr-12">
                  <v-col cols="8">
                    <v-file-input
                      v-model="file"
                      truncate-length="40"
                      accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                      label="Click here to upload an Excel file"
                      @change="handleFileUpload"
                    ></v-file-input>
                  </v-col>
                  <v-spacer></v-spacer>
                  <v-col cols="4">
                    <v-select
                      v-model="selectedZone"
                      :items="zones"
                      label="Select Zone"
                      persistent-hint
                      hint="Select a zone to import assets to."
                      item-text="name"
                      item-value="_id"
                      :rules="rules.requiredZone"
                      required
                      outlined
                    ></v-select>
                  </v-col>
                </v-row>
                <v-row class="mx-8">
                  <v-btn
                    :loading="btnLoading"
                    color="primary"
                    :disabled="file === null"
                    @click="validate"
                  >
                    Import <v-icon right>mdi-upload</v-icon>
                  </v-btn>
                  <v-spacer></v-spacer>
                  <v-btn
                    class="mr-6"
                    dark
                    @click="showHeaderDialog = true"
                    color="primary"
                    text
                    :disabled="file === null"
                  > Map headers
                  </v-btn>
                </v-row>
              </v-form>
              <div class="my-12"></div>
              <v-data-table
                v-if="tablePreviewData.length > 0 && headers.length > 1"
                :headers="headers"
                :items="tablePreviewData"
                :items-per-page="25"
                :footer-props="{
                  'items-per-page-options': [10, 25, 100]
                }"
              >
              <template slot="item" slot-scope="props">
                <tr>
                  <template v-for="(header, key) in selectedHeaders">
                    <td :key="key">{{ props.item[header] }}</td>
                  </template>
                  <td>
                    <v-icon v-if="props.item.Status === 'waiting'" color="orange">mdi-minus</v-icon>
                    <v-progress-circular
                      v-else-if="props.item.Status === 'uploading'"
                      color="blue"
                      indeterminate
                    ></v-progress-circular>
                    <v-icon v-else-if="props.item.Status === 'complete'" color="green">mdi-check</v-icon>
                    <v-tooltip top v-else-if="props.item.Status === 'complete duplicate'" color="green">
                      <template v-slot:activator="{ on }">
                        <v-icon v-on="on" color="green">mdi-check-underline</v-icon>
                      </template>
                      <span>Duplicate tag detected, inserted missing information.</span>
                    </v-tooltip>
                    <v-tooltip top v-else-if="props.item.Status === 'duplicate detected'" color="orange">
                      <template v-slot:activator="{ on }">
                        <v-icon v-on="on" color="orange">mdi-progress-alert</v-icon>
                      </template>
                      <span>Duplicate tag detected, attempting to update existing item.</span>
                    </v-tooltip>
                    <v-tooltip top v-else color="red">
                      <template v-slot:activator="{ on }">
                        <v-icon v-on="on" color="red">mdi-close</v-icon>
                      </template>
                      <span>Error: {{ props.item.Status }}</span>
                    </v-tooltip>
                  </td>
                </tr>
              </template>
            </v-data-table>
            </v-col>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <error-snackbar v-model="errorText"></error-snackbar>
    <v-dialog v-model="showHeaderDialog" style="z-index: 1001;" max-width="80%">
      <v-card>
        <v-card-text>
          <v-col>
            <v-row align-content="center" class="my-2">
              <v-btn
              text
              class="my-2"
              elevation="0"
              small
                @click="() => {selectedHeaders.push(''); dataFieldMapping.push('')}"><v-icon color="green">mdi-plus</v-icon> Add header mapping
              </v-btn>
              <v-spacer></v-spacer>
              <v-btn
                dark
                color="green"
                @click="saveHeaderMapping"> Save <v-icon class="mx-2"> mdi-content-save</v-icon>
              </v-btn>
            </v-row>
            <template v-for="(item, i) in selectedHeaders">
              <v-row :key="i" align="baseline" justify="space-around">
                <v-col cols="5">
                  <v-select
                    dense
                    v-model="selectedHeaders[i]"
                    :items="detectedHeaders.filter(header => !selectedHeaders.includes(header) || header === selectedHeaders[i])"
                    label="Select Header"
                    outlined
                  ></v-select>
                </v-col>
                <v-col cols="1" align="center">
                  <v-icon dense align-self="center" >
                    mdi-arrow-right
                </v-icon>
                </v-col>
                <v-col cols="5" >
                  <v-select
                    dense
                    v-model="dataFieldMapping[i]"
                    :items="possibleMappings.filter(field => !dataFieldMapping.includes(field) || field === dataFieldMapping[i])"
                    label="Select Asset Field"
                    outlined
                  ></v-select>
                </v-col>
                <v-col cols="1">
                  <v-btn
                    icon
                    @click="() => removeHeaderMapping(i)"
                  > <v-icon color="error" dense>mdi-minus</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </template>
          </v-col>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            @click="() => showHeaderDialog = false"
            text
            color="red"
            >Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>
<script>
import ApiRequests from '@/api/ApiRequests'
import * as XLSX from 'xlsx'
import { ENDPOINT } from '@/shared/constants'
import { mapGetters, mapActions } from 'vuex'
import axios from 'axios'
import ErrorSnackbar from 'vue-common/components/ErrorSnackbar.vue'

export default {
  components: {
    ErrorSnackbar
  },

  data () {
    return {
      endpoint: ENDPOINT,
      errorText: '',
      file: null, // Import file
      showHeaderDialog: false, // Dialog for header mapping
      tablePreviewData: [], // Table data for preview
      headers: [], // Table headers
      selectedZone: null, // Zone for event
      zones: [], // Zone selection for drop down
      detectedHeaders: [], // Detected headers in import file
      selectedHeaders: [], // Selected headers for mapping
      dataFieldMapping: [], // Mapping of headers to asset fields
      possibleMappings: [], // Thing data fields
      // Uploading
      btnLoading: false,
      reader: new FileReader(),
      parsedData: null,
      categories: [],
      // Rules
      rules: {
        requiredZone: [value => !!value || 'Please select a zone to import assets to.']
      }
    }
  },

  created () {
    console.log('Site Settings:', this.siteSettings)

    // We'll grab the site settings when index fetches, defer to the watcher
    if (Object.keys(this.siteSettings).length > 0) {
      this.getHeaderFieldMapping()
    }

    this.categories = JSON.parse(JSON.stringify(this.getCategories)) // Deep copy
    // TODO - Fetch site settings is OrgID is null
    // Sometimes during a refresh this can be empty
    ApiRequests.getZones().then(r => {
      this.zones = r.data
    }).catch(e => {
      console.log(e)
    })

    this.possibleMappings = this.generatePossibleMappings()
  },

  watch: {
    file: function () {
      if (this.file === null) {
        this.reader.abort()
        this.paredData = null
        this.tablePreviewData = []
      }
    },

    siteSettings: function () {
      this.getHeaderFieldMapping()
    },

    selectedHeaders: function () {
      this.headers = []
      for (let i = 0; i < this.selectedHeaders.length; i++) {
        this.headers.push({ text: this.selectedHeaders[i], value: this.selectedHeaders[i] })
      }
      this.headers.push({ text: 'Status', value: 'Status' })
    }
  },

  computed: {
    ...mapGetters([
      'siteSettings',
      'getCategories'
    ])
  },

  methods: {
    ...mapActions([
      'fetchCategories',
      'getSiteSettings'
    ]),

    saveHeaderMapping () {
      let tempObj = {}
      tempObj.name = 'DEFAULT'
      let headers = this.selectedHeaders
      let assetFields = this.dataFieldMapping
      let tempMap = {}
      for (let i = 0; i < headers.length; i++) {
        tempMap[headers[i]] = assetFields[i]
      }

      tempObj.headerAssetFields = tempMap

      let siteSettings = JSON.parse(JSON.stringify(this.siteSettings))
      if (siteSettings.importSettings.importMappings !== null) {
        siteSettings.importSettings.importMappings[0] = tempObj
      } else {
        siteSettings.importSettings.importMappings = [tempObj]
      }

      ApiRequests.putSiteSettings(siteSettings).then(r => {
        console.log(r)
        this.errorText = 'Header mapping saved'
        // Refresh settings after saving
        this.getSiteSettings()
      }).catch(e => {
        console.log(e)
        this.errorText = e
      })
    },

    async processImportCategory (importCategory) {
      // Check if category exists else create new category
      let arr = []
      let found = false
      for (var category of this.categories) {
        if (category.name === importCategory) {
          found = true
          break
        }
      }

      if (!found) {
        await axios.post(ENDPOINT + 'image-categories', {name: importCategory})
          .then(async (res) => {
            console.log(res.data)
            arr.push(res.data.id)
            this.categories.push(res.data)
            this.errorText = `Created new category: ${importCategory}`
            // Refesh categories
            await this.fetchCategories()
            console.log(this.getCategories)
          })
          .catch(error => {
            console.log(error)
            this.errorText = error
          })
      } else {
        for (var x in this.categories) {
          if (this.categories[x].name === importCategory) {
            arr.push(this.categories[x].id)
          }
        }
      }
      return arr
    },

    async uploadAssets () {
      this.btnLoading = true
      let successfulUploads = 0
      for (let i = 0; i < this.tablePreviewData.length; i++) {
        let error = null
        let statusMessage = 'complete'
        this.tablePreviewData[i].Status = 'uploading'

        let currItem = {}
        currItem = await this.prepareItem(this.tablePreviewData[i])
        currItem.common.class = 'item'

        await ApiRequests.postThing(currItem)
          .then((res) => {
            successfulUploads++
          })
          .catch(async err => {
            console.log('Post thing error: ', err.response)
            // Check if error is due to a bad request, then for duplicate
            if (err.response.status === 400 &&
                err.response.data.includes('duplicate')) {
              this.tablePreviewData[i].Status = 'duplicate detected'
              statusMessage += ' duplicate'
              // This tag has probably been added w/ prefix through handheld
              // ImportSettings.prefixedTagDupCheck
              await ApiRequests.getThingByTag(
                this.siteSettings.importSettings.prefixedTagDupCheck
                ? this.siteSettings.orgTagPrefix + currItem.common.tags[0].toString()
                : currItem.common.tags[0].toString())
                .then(async response => {
                  console.log(response)
                  let thing = response.data[0]
                  currItem._id = thing._id
                  await axios.put(ENDPOINT + 'things/' + currItem._id, currItem)
                    .then(response => {
                      successfulUploads++
                      console.log(response)
                    })
                    .catch(err => {
                      this.tablePreviewData[i].Status = err.response.data
                      error = err
                      console.log(err)
                    })
                })
                .catch(err => {
                  this.tablePreviewData[i].Status = err
                  error = err
                  console.log(err)
                })
            } else {
              this.tablePreviewData[i].Status = err.response.data
              error = err
              console.log(err)
            }
          })
        if (error === null && this.selectedZone !== null) { // Skip HTTP Event if error in item upload || no tags imported
          await this.createImportEvent(currItem.common.tags, this.selectedZone)
        }
        this.tablePreviewData[i].Status = statusMessage
      }
      this.errorText = `${successfulUploads} of ${this.tablePreviewData.length} items uploaded`
      this.btnLoading = false
    },

    async createImportEvent (tags, zone) {
      if (tags != null && tags.length > 0) {
        await ApiRequests.postHttpEvent({tag: tags[0], zone: zone})
          .then((res) => {
            console.log(res)
          })
          .catch(error => {
            console.log(error)
          })
      }
    },

    handleFileUpload () {
      const file = this.file

      this.reader.onload = (e) => {
        const data = new Uint8Array(e.target.result)
        const workbook = XLSX.read(data, { type: 'array' })

        // Assuming ALL Details is first sheet
        const sheetName = workbook.SheetNames[0]
        const sheet = workbook.Sheets[sheetName]

        // Parsing sheet data
        const parsedData = XLSX.utils.sheet_to_json(sheet, { header: 1 })
        this.detectedHeaders = parsedData[0]

        // Trim empty rows
        this.parsedData = parsedData.filter(subArray => subArray.some(item => item !== undefined && item !== null))

        this.prepareTablePreview()
      }
      this.reader.readAsArrayBuffer(file)
    },

    prepareTablePreview () {
      this.tablePreviewData = []
      for (let i = 1; i < this.parsedData.length; i++) {
        let row = {}
        for (let j = 0; j < this.parsedData[i].length; j++) {
          row[this.detectedHeaders[j]] = this.parsedData[i][j]
        }
        row.Status = 'waiting'
        this.tablePreviewData.push(row)
      }
    },

    async prepareItem (row) {
      let filteredItem = {}
      let currItem = {}
      currItem.common = {}
      Object.keys(row).forEach(key => {
        if (this.selectedHeaders.includes(key)) {
          filteredItem[key] = row[key]
        }
      })

      for (var i = 0; i < this.selectedHeaders.length; i++) {
        await this.itemMappingSwitch(this.dataFieldMapping[i], this.selectedHeaders[i], filteredItem, currItem)
      }
      return currItem
    },

    async itemMappingSwitch (fieldName, headerName, row, thing) {
      switch (fieldName) {
        case 'name':
          thing.common.name = row[headerName].toString()
          break
        case 'description':
          thing.common.description = row[headerName].toString()
          break
        case 'serial':
          thing.common.serial = row[headerName].toString()
          break
        case 'tags':
          if (typeof thing.common.tags === 'undefined') {
            thing.common.tags = []
          }
          thing.common.tags.push(row[headerName].toString())
          // Check if we need to make a tag w/ prefix
          if (this.siteSettings.orgTagPrefix !== null &&
            this.siteSettings.orgTagPrefix !== '' &&
            this.siteSettings.importSettings.generatePrefixTag &&
            !row[headerName].startsWith(this.siteSettings.orgTagPrefix)) {
            thing.common.tags.push(this.siteSettings.orgTagPrefix + row[headerName].toString())
          }
          break
        case 'tags1':
          thing.common.tags1 = [row[headerName].toString()]
          break
        case 'tags2':
          thing.common.tags2 = [row[headerName].toString()]
          break
        case 'categories':
          thing.common.categories = await this.processImportCategory(row[headerName])
          break
        case 'labels':
          thing.common.labels = [row[headerName].toString()]
          break
        default:
          console.log('No mapping found')
      }
    },

    generatePossibleMappings () {
      const itemMappings = [
        'name',
        'description',
        'serial',
        'tags',
        'tags1',
        'tags2',
        'categories',
        'labels'
      ]
      return itemMappings
    },

    getHeaderFieldMapping () {
      if (this.siteSettings.importSettings.importMappings !== null &&
          this.siteSettings.importSettings.importMappings.length > 0) {
        const headerAssetFields = this.siteSettings.importSettings.importMappings[0].headerAssetFields
        this.selectedHeaders = Object.keys(headerAssetFields)
        this.dataFieldMapping = Object.values(headerAssetFields)
      }
    },

    removeHeaderMapping: function (index) {
      this.selectedHeaders.splice(index, 1)
      this.dataFieldMapping.splice(index, 1)
    },

    validate () {
      if (this.selectedHeaders.length > 0 && this.dataFieldMapping.length > 0) {
        if (!this.siteSettings.importSettings.requireZone) {
          this.uploadAssets()
          return
        } else {
          if (this.$refs.form.validate()) {
            this.uploadAssets()
            return
          }
        }
      }

      let message = ''
      if (this.selectedHeaders.length < 1 || this.dataFieldMapping.length < 1) {
        message = 'Please map headers to asset fields.'
      } else {
        message = 'Please select a zone.'
      }
      // Make generic if more validation is added
      this.errorText = message
    }
  }
}
</script>
<style scoped>
  .v-text-field input::placeholder {
    color: red;
  }
</style>




// WEBPACK FOOTER //
// src/views/ImportAsset.vue